import { Controller } from "@hotwired/stimulus"

// General-purpose toast notification system.
// Attach to a container: <div data-controller="flash-toast" ...>
// Call from anywhere: window.showToast("success", "Photo uploaded!")
//
// Types: success (green, 5s), error (red, persist), warning (amber, 8s), info (blue, 5s)
export default class extends Controller {
  static values = { autoShow: { type: Array, default: [] } }

  connect() {
    // Kept so disconnect can tell whether window.showToast is still ours
    // (bind makes a new function each call, so comparing two binds never
    // matched and the old pointer outlived its element).
    this.boundShow = (type, message) => this.show(type, this.viewAsMessage(type, message))
    window.showToast = this.boundShow

    // Auto-show toasts passed via data attribute (for flash messages)
    if (this.autoShowValue.length > 0) {
      this.autoShowValue.forEach(({ type, message }) => {
        this.show(type, message)
      })
    }
  }

  disconnect() {
    // Assign rather than delete: a page script that declared its own
    // function showToast (SPT Contact Finder) makes the global
    // non-configurable, and delete throws in strict mode.
    if (window.showToast === this.boundShow) {
      window.showToast = undefined
    }
  }

  show(type, message) {
    // The same error already on screen (a retry that failed again) is not
    // stacked a second time. Errors only: a repeated success ("Photo link
    // copied!") should still show each time.
    if (type === "error" && [...this.element.querySelectorAll(".toast-notification p")].some((p) => p.textContent === message)) return
    const colors = {
      success: { border: "#9CAF88", icon: "#9CAF88" },
      error:   { border: "#E57373", icon: "#E57373" },
      warning: { border: "#F5A623", icon: "#F5A623" },
      info:    { border: "#6BA3C7", icon: "#6BA3C7" }
    }
    const c = colors[type] || colors.info

    const toast = document.createElement("div")
    toast.className = "toast-notification transform translate-x-full transition-transform duration-300 ease-out"
    toast.setAttribute("role", "alert")

    const icon = this.iconFor(type, c.icon)

    toast.innerHTML = `
      <div class="flex items-start gap-3 p-4 bg-white rounded-lg shadow-lg border border-[#E8E4DF] max-w-sm" style="border-left: 4px solid ${c.border}">
        <div class="flex-shrink-0 mt-0.5">${icon}</div>
        <div class="flex-1 min-w-0">
          <p class="text-sm text-[#2D2D2D]">${this.esc(message)}</p>
        </div>
        <button class="flex-shrink-0 text-[#8C8C8C] hover:text-[#2D2D2D] transition-colors" onclick="this.closest('.toast-notification').dispatchEvent(new Event('dismiss'))">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `

    toast.addEventListener("dismiss", () => this.removeToast(toast))
    this.element.appendChild(toast)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.classList.remove("translate-x-full")
        toast.classList.add("translate-x-0")
      })
    })

    const durations = { success: 5000, error: null, warning: 8000, info: 5000 }
    const duration = durations[type]
    if (duration) {
      setTimeout(() => this.removeToast(toast), duration)
    }
  }

  // While a staffer views an intern, a script's error pop-up ("Try again")
  // would be untrue: nothing can be saved. The bar carries the words to
  // show instead (words seat W4, 2026-10-17). Flash messages shown on load
  // keep their own words.
  viewAsMessage(type, message) {
    if (type !== "error") return message
    const bar = document.getElementById("view-as-bar")
    return (bar && bar.dataset.viewAsRefusal) || message
  }

  // Action for buttons: data-action="click->flash-toast#showFromButton"
  // data-toast-type="warning" data-toast-message="Some message"
  showFromButton(event) {
    const btn = event.currentTarget
    const type = btn.dataset.toastType || "info"
    const message = btn.dataset.toastMessage || ""
    if (message) this.show(type, message)
  }

  removeToast(toast) {
    if (!toast.parentNode) return
    toast.classList.remove("translate-x-0")
    toast.classList.add("translate-x-full")
    setTimeout(() => toast.remove(), 300)
  }

  iconFor(type, color) {
    if (type === "error") {
      return `<svg class="w-5 h-5 flex-shrink-0" fill="${color}" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>`
    } else if (type === "warning") {
      return `<svg class="w-5 h-5 flex-shrink-0" fill="${color}" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>`
    } else if (type === "success") {
      return `<svg class="w-5 h-5 flex-shrink-0" fill="${color}" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>`
    } else {
      return `<svg class="w-5 h-5 flex-shrink-0" fill="${color}" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>`
    }
  }

  esc(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }
}
